import { useEffect } from "react";
import { setCanonical, setMetaTags, SpeakableSchema } from "@/components/seo/SchemaMarkup";

export default function PrivacyPolicyPage() {
  useEffect(() => {
    setCanonical("/privacy-policy");
    setMetaTags({ title: "Privacy Policy | Storajini Guide", description: "Privacy Policy for Storajini Guide - how we collect, use, and protect your data." });
  }, []);

  return (
    <div data-testid="privacy-policy-page">
      <SpeakableSchema selectors={["h1"]} />
      <div className="py-16">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-extrabold text-gray-900 mb-8">Privacy Policy</h1>
        <div className="prose max-w-none space-y-6 text-gray-600 leading-relaxed">
          <p><strong>Last updated:</strong> March 2026</p>
          
          <p>Storajini Guide ("we," "our," or "us") operates the website storajini.net as a companion resource to Storajini.com. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website.</p>

          <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">Information We Collect</h2>
          <p><strong>Personal Data:</strong> When you fill out a contact or inquiry form, we may collect your name, email address, phone number, and message content. We only collect this information when you voluntarily provide it.</p>
          <p><strong>Usage Data:</strong> We automatically collect certain information when you visit our site, including your IP address, browser type, operating system, referring URLs, pages viewed, and time spent on pages.</p>
          <p><strong>Cookies:</strong> We use cookies and similar tracking technologies to enhance your browsing experience. See our <a href="/cookie-policy" className="text-amber-600 underline">Cookie Policy</a> for details.</p>

          <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">How We Use Your Information</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>To respond to your inquiries and connect you with storage/parking solutions</li>
            <li>To improve our website content and user experience</li>
            <li>To analyze website traffic and usage patterns</li>
            <li>To send you relevant information if you opt in to communications</li>
            <li>To comply with legal obligations</li>
          </ul>

          <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">Data Sharing</h2>
          <p>We may share your information with Storajini.com (our parent platform) to facilitate storage and parking inquiries. We do not sell your personal information to third parties.</p>

          <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">Philippines Data Privacy Act (RA 10173)</h2>
          <p>We comply with the Philippines Data Privacy Act of 2012 (Republic Act No. 10173) and its Implementing Rules and Regulations. As a data subject, you have the following rights:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Right to be Informed</strong> - You have the right to know how your data is being processed</li>
            <li><strong>Right to Access</strong> - You may request access to your personal data</li>
            <li><strong>Right to Rectification</strong> - You may request correction of inaccurate data</li>
            <li><strong>Right to Erasure</strong> - You may request deletion of your personal data</li>
            <li><strong>Right to Object</strong> - You may object to the processing of your data</li>
            <li><strong>Right to Data Portability</strong> - You may request a copy of your data in a structured format</li>
          </ul>

          <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">International Users</h2>
          <p>If you are accessing our website from outside the Philippines, please be aware that your data may be transferred to and processed in the Philippines. By using our website, you consent to this transfer. We also respect international privacy regulations including GDPR for EU residents and CCPA for California residents.</p>

          <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">AI Assistant Disclosure</h2>
          <p>Our website features an AI-powered chat assistant named "Sarah." Sarah is an artificial intelligence and not a human. Conversations with Sarah are processed to provide helpful responses about storage, parking, and moving in the Philippines. Chat data may be stored to improve our service quality.</p>

          <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">Data Security</h2>
          <p>We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction.</p>

          <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">Contact Us</h2>
          <p>For any privacy-related concerns or to exercise your data rights, contact us at:</p>
          <p><strong>Email:</strong> support@storajini.com<br />
          <strong>Address:</strong> 103 10th street New Manila, Quezon City 1112, brgy. Mariana, Philippines</p>
        </div>
      </div>
      </div>
    </div>
  );
}
